/**
 * Hook: useListPage
 *
 * WorksList / EventsList 共通の URL パラメータ管理・フィルタ状態・
 * 検索クエリデバウンスを一元管理する汎用 hook。
 *
 * 各ページ固有のロジック（フィルタ適用・検索・ソート）は
 * 呼び出し元に委譲し、共通の状態管理のみを担う。
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchFilter } from './useSearchFilter';

/** フィルタの最小要件 */
export interface BaseFilters {
  searchQuery: string;
}

export interface UseListPageOptions<TFilters extends BaseFilters> {
  /**
   * URL SearchParams から TFilters を構築する関数。
   * dateRangeLimit など外部データに依存する場合は useCallback でメモ化すること。
   */
  parseFilters: (params: URLSearchParams) => TFilters;

  /**
   * TFilters から URL SearchParams を構築する関数。
   * 同上。
   */
  buildParams: (filters: TFilters) => URLSearchParams;
}

export interface UseListPageResult<TFilters extends BaseFilters> {
  /** URL から解析された現在のフィルタ値 */
  filters: TFilters;

  /** フィルタを更新して URL に反映する */
  updateFilters: (filters: TFilters, options?: { replace?: boolean }) => void;

  /** 検索バー用のローカルクエリ（デバウンス前） */
  localSearchQuery: string;

  /** 検索バー入力のセッター */
  setLocalSearchQuery: (query: string) => void;

  /** フィルタパネルの開閉状態 */
  isFilterOpen: boolean;

  /** フィルタパネルの開閉を切り替える */
  setIsFilterOpen: (open: boolean) => void;
}

/**
 * リストページ共通の状態管理 hook
 *
 * 使用例（WorksList）:
 * ```ts
 * const { filters, updateFilters, localSearchQuery, setLocalSearchQuery, isFilterOpen, setIsFilterOpen } =
 *   useListPage({ parseFilters, buildParams });
 * ```
 */
export function useListPage<TFilters extends BaseFilters>({
  parseFilters,
  buildParams,
}: UseListPageOptions<TFilters>): UseListPageResult<TFilters> {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // parseFilters の参照が変わったとき（例: dateRangeLimit ロード後）も再計算する
  const filters = useMemo(
    () => parseFilters(searchParams),
    [searchParams, parseFilters]
  );

  const updateFilters = useCallback(
    (nextFilters: TFilters, options?: { replace?: boolean }) => {
      setSearchParams(buildParams(nextFilters), options);
    },
    [setSearchParams, buildParams]
  );

  const { localQuery: localSearchQuery, setLocalQuery: setLocalSearchQuery } =
    useSearchFilter(filters.searchQuery, (query) =>
      updateFilters({ ...filters, searchQuery: query } as TFilters, {
        replace: true,
      })
    );

  return {
    filters,
    updateFilters,
    localSearchQuery,
    setLocalSearchQuery,
    isFilterOpen,
    setIsFilterOpen,
  };
}
